import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Upload, FileText, FileSpreadsheet, File, Check, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = "";

const UploadPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const validExtensions = ['csv', 'xlsx', 'xls', 'pdf'];
    const validFiles = newFiles.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return validExtensions.includes(ext);
    });
    
    if (validFiles.length !== newFiles.length) {
      toast.error('Some files were skipped. Only CSV, Excel, and PDF files are supported.');
    }
    
    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return <FileText className="w-5 h-5 text-red-500" />;
    if (['xlsx', 'xls'].includes(ext)) return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
    if (ext === 'csv') return <FileSpreadsheet className="w-5 h-5 text-blue-600" />;
    return <File className="w-5 h-5 text-zinc-500" />;
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    const results = [];
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post(`${API_URL}/api/files/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        results.push({ file: file.name, success: true, data: response.data });
        toast.success(`${file.name} uploaded successfully`);
      } catch (e) {
        results.push({ file: file.name, success: false, error: e.response?.data?.detail || e.message });
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploadResults(results);
    setUploading(false);
    setFiles([]);
  };

  if (!isAdmin) {
    return (
      <div className="p-6" data-testid="upload-page">
        <Card className="border-zinc-200 bg-white">
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-zinc-950 mb-2">Admin Access Required</h2>
            <p className="text-zinc-500">Only administrators can upload files.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="upload-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Upload Files</h1>
        <p className="text-zinc-500 mt-1">Upload Excel, CSV, or PDF files for analysis</p>
      </div>

      {/* Upload Zone */}
      <Card className="border-zinc-200 bg-white mb-6">
        <CardContent className="p-6">
          <div
            className={`dropzone ${dragActive ? 'active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input').click()}
            data-testid="upload-dropzone"
          >
            <input
              id="file-input"
              type="file"
              multiple
              accept=".csv,.xlsx,.xls,.pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Upload className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-zinc-950 mb-1">
              Drag and drop files here
            </p>
            <p className="text-sm text-zinc-500 mb-4">
              or click to browse
            </p>
            <p className="text-xs text-zinc-400">
              Supported formats: CSV, Excel (.xlsx, .xls), PDF
            </p>
          </div>
          <p className="text-xs text-zinc-400 mt-3">
            Vannamei export files: alongside the usual Species / Process / Form / Count Grade columns,
            a monthly file may also include <span className="font-mono">Correct Species</span>,{' '}
            <span className="font-mono">Correct Process</span>, <span className="font-mono">Correct Form</span>,
            and <span className="font-mono">Correct Grade</span> — when filled in, these take priority over
            the base column for that row, and Category is recalculated to match. A{' '}
            <span className="font-mono">Particulars</span> column is treated the same as{' '}
            <span className="font-mono">Product Description</span>. Files without any of these still upload
            exactly as before.
          </p>
        </CardContent>
      </Card>

      {/* Selected Files */}
      {files.length > 0 && (
        <Card className="border-zinc-200 bg-white mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-zinc-950">
              Selected Files ({files.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {files.map((file, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {getFileIcon(file.name)}
                    <div>
                      <p className="text-sm font-medium text-zinc-950">{file.name}</p>
                      <p className="text-xs text-zinc-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                    className="text-zinc-400 hover:text-zinc-600"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            
            <div className="mt-4 flex gap-3">
              <Button
                onClick={uploadFiles}
                disabled={uploading}
                className="bg-blue-700 hover:bg-blue-800 text-white"
                data-testid="upload-button"
              >
                {uploading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Uploading...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Upload {files.length} {files.length === 1 ? 'file' : 'files'}
                  </span>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setFiles([])}
                disabled={uploading}
                className="border-zinc-200"
              >
                Clear All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Results */}
      {uploadResults.length > 0 && (
        <Card className="border-zinc-200 bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-zinc-950">
              Upload Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {uploadResults.map((result, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {result.success ? (
                      <Check className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-zinc-950">{result.file}</p>
                      {result.success ? (
                        <p className="text-xs text-green-600">
                          {result.data.row_count} rows • {result.data.columns?.length || 0} columns
                        </p>
                      ) : (
                        <p className="text-xs text-red-600">{result.error}</p>
                      )}
                    </div>
                  </div>
                  {result.success && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/files/${result.data.id}`)}
                      className="text-blue-700 hover:text-blue-800"
                    >
                      View
                    </Button>
                  )}
                </div>
              ))}
            </div>
            
            <Button
              variant="outline"
              className="mt-4 border-zinc-200"
              onClick={() => setUploadResults([])}
            >
              Clear Results
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UploadPage;
